import { useState, useEffect } from "react";
import Layout from "../components/Layout.jsx";
import { analytics, getAccount } from "../lib/supabase.js";
import MarketingTab from "../components/MarketingTab.jsx";
import FunnelOverview from "../components/FunnelOverview.jsx";

// ─── Font loader ─────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

// ─── Theme ───────────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const SENTIMENT_COLORS = { positive: "#15803d", neutral: "#8a8a8a", negative: "#b45309", frustrated: "#dc2626", unanalyzed: "#c0bdb8" };
const SENTIMENT_BG = { positive: "#f0fdf4", neutral: "#f6f5f2", negative: "#fffbeb", frustrated: "#fef2f2", unanalyzed: "#f6f5f2" };
const SENTIMENT_ICONS = { positive: "😊", neutral: "😐", negative: "😟", frustrated: "😤" };

// ─── Shared UI helpers ───────────────────────────────────────────────────────

const card = { background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 22 };
const secHead = (text) => <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 14 }}>{text}</div>;
const empty = (text) => <div style={{ color: L.dim, fontSize: 13, padding: "24px 0", textAlign: "center" }}>{text}</div>;

function BarTrack({ pct, color }) {
  return (
    <div style={{ height: 3, background: L.borderLight, borderRadius: 2 }}>
      <div style={{ height: 3, borderRadius: 2, background: color || L.text, width: `${Math.max(pct, 0)}%`, transition: "width 0.3s" }} />
    </div>
  );
}

function RankedList({ items, labelKey, valueKey, maxValue, color }) {
  return items.map((item, i) => (
    <div key={item[labelKey] || i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
      <span style={{ fontSize: 10, color: L.dim, fontWeight: 600, width: 16, textAlign: "right" }}>{i + 1}</span>
      <div style={{ flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 12, color: L.text }}>{item[labelKey]}</span>
          <span style={{ color: L.muted, fontVariantNumeric: "tabular-nums", fontSize: 12 }}>{item[valueKey]}</span>
        </div>
        <BarTrack pct={(item[valueKey] / (maxValue || 1)) * 100} color={color} />
      </div>
    </div>
  ));
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function Analytics() {
  useFonts();
  const color = getAccount()?.widget_color || "#6366f1";
  const [days, setDays] = useState(30);
  const [section, setSection] = useState("overview");
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [intents, setIntents] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [responseStats, setResponseStats] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [intentTags, setIntentTags] = useState([]);
  const [sentimentTrend, setSentimentTrend] = useState([]);
  const [productMentions, setProductMentions] = useState([]);
  const [productOpps, setProductOpps] = useState([]);
  const [lowMatch, setLowMatch] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [hourly, setHourly] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [teamPerf, setTeamPerf] = useState([]);
  const [assignOverview, setAssignOverview] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        analytics.summary(days), analytics.dailyMetrics(days), analytics.intents(days),
        analytics.contentGaps(), analytics.responseStats(days), analytics.sentimentBreakdown(days),
        analytics.intentTags(days), analytics.sentimentTrend(days), analytics.productMentions(days),
        analytics.productOpportunities(days), analytics.lowMatchQueries(days), analytics.conversationFunnel(days),
        analytics.hourlyDistribution(days), analytics.topPages(days), analytics.teamPerformance(days),
        analytics.assignmentOverview(days),
      ]);
      const val = (i) => results[i]?.status === "fulfilled" ? results[i].value?.data : null;
      setSummary(val(0) || {}); setDaily(Array.isArray(val(1)) ? val(1) : []);
      setIntents(Array.isArray(val(2)) ? val(2) : []); setGaps(Array.isArray(val(3)) ? val(3) : []);
      setResponseStats(val(4) || {}); setSentiment(val(5) || {});
      setIntentTags(Array.isArray(val(6)) ? val(6) : []); setSentimentTrend(Array.isArray(val(7)) ? val(7) : []);
      setProductMentions(Array.isArray(val(8)) ? val(8) : []); setProductOpps(Array.isArray(val(9)) ? val(9) : []);
      setLowMatch(Array.isArray(val(10)) ? val(10) : []); setFunnel(val(11) || {});
      setHourly(Array.isArray(val(12)) ? val(12) : []); setTopPages(Array.isArray(val(13)) ? val(13) : []);
      setTeamPerf(Array.isArray(val(14)) ? val(14) : []); setAssignOverview(val(15) || {});
    } catch (e) { console.error("Analytics load error:", e); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [days]);

  const s = summary || {};
  const totalSessions = s.total_sessions || 0;
  const totalMessages = s.total_messages || 0;
  const resolved = s.resolved || 0;
  const resRate = totalSessions > 0 ? Math.round((resolved / totalSessions) * 100) : 0;
  const maxDailySessions = Math.max(1, ...daily.map(d => d.chat_sessions || 0));

  const sections = [
    { key: "overview", label: "Overview" },
    { key: "intelligence", label: "Conversation intelligence" },
    { key: "products", label: "Product insights" },
    { key: "funnel", label: "Widget & funnel" },
    { key: "team", label: "Team performance" },
    { key: "marketing", label: "Marketing" },
  ];

  if (loading) return <Layout><div style={{ padding: 40, textAlign: "center", color: L.muted, background: L.bg, margin: -28, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: sans }}>Loading analytics...</div></Layout>;

  return (
    <Layout>
      <style>{`@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div style={{ minHeight: "calc(100vh - 56px)", background: L.bg, margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, animation: "ov-up 0.5s ease both" }}>
          <div>
            <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Analytics</h1>
            <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Chat performance, conversation intelligence, and team insights</p>
          </div>
          <div style={{ display: "flex", gap: 2, background: L.inputBg, borderRadius: 8, border: `1px solid ${L.border}`, padding: 2 }}>
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)} style={{
                padding: "6px 14px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                cursor: "pointer", fontFamily: sans, border: "none",
                background: days === d ? L.surface : "transparent",
                color: days === d ? L.text : L.muted,
                boxShadow: days === d ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
              }}>{d}d</button>
            ))}
          </div>
        </div>

        {/* Section tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.05s both" }}>
          {sections.map(sec => {
            const active = section === sec.key;
            return (
              <button key={sec.key} onClick={() => setSection(sec.key)} style={{
                padding: "8px 16px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                cursor: "pointer", fontFamily: sans, transition: "all 0.15s",
                background: active ? L.text : "none",
                color: active ? L.bg : L.muted,
                border: `1px solid ${active ? L.text : L.border}`,
              }}>{sec.label}</button>
            );
          })}
        </div>

        <div style={{ animation: "ov-up 0.5s ease 0.1s both" }}>

        {/* ═══ OVERVIEW ═══ */}
        {section === "overview" && (<>
          <FunnelOverview days={days} />
          <div style={{ display: "flex", gap: 0, marginBottom: 20, borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}` }}>
            {[
              { label: "Sessions", value: totalSessions },
              { label: "Messages", value: totalMessages },
              { label: "Resolution rate", value: `${resRate}%`, highlight: resRate >= 50 },
              { label: "Unique visitors", value: s.unique_visitors || 0 },
              { label: "Tokens used", value: (s.total_tokens || 0).toLocaleString() },
            ].map((c, i) => (
              <div key={c.label} style={{ padding: "16px 18px", flex: 1, borderRight: i < 4 ? `1px solid ${L.border}` : "none" }}>
                <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 4 }}>{c.label}</div>
                <div style={{ fontSize: 26, fontWeight: 300, letterSpacing: "-0.02em", color: c.highlight ? "#15803d" : L.text }}>{c.value}</div>
              </div>
            ))}
          </div>

          {responseStats && (
            <div style={{ display: "flex", gap: 0, marginBottom: 20, borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}` }}>
              {[
                { label: "AI responses", value: responseStats.total_responses || 0 },
                { label: "Avg latency", value: responseStats.avg_latency_ms ? `${responseStats.avg_latency_ms}ms` : "—" },
                { label: "Avg tokens/response", value: responseStats.avg_tokens || "—" },
                { label: "Avg msgs/session", value: s.avg_messages_per_session || "—" },
              ].map((c, i) => (
                <div key={c.label} style={{ padding: "14px 18px", flex: 1, borderRight: i < 3 ? `1px solid ${L.border}` : "none" }}>
                  <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 20, fontWeight: 300, letterSpacing: "-0.02em" }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Daily chart */}
          <div style={{ ...card, marginBottom: 20 }}>
            {secHead("Daily activity")}
            {daily.length === 0 ? empty("No activity data yet.") : (
              <>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 140, overflowX: "auto", paddingBottom: 24, position: "relative" }}>
                  {daily.map(d => {
                    const sessions = d.chat_sessions || 0;
                    const barH = Math.max(2, (sessions / maxDailySessions) * 110);
                    const dateStr = new Date(d.metric_date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
                    const isToday = d.metric_date === new Date().toISOString().split("T")[0];
                    return (
                      <div key={d.metric_date} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: `0 0 ${Math.max(100 / daily.length, 3)}%`, minWidth: 16, position: "relative" }} title={`${dateStr}: ${sessions} sessions`}>
                        <div style={{ width: 10, height: barH, background: isToday ? L.text : L.dim, borderRadius: "3px 3px 0 0", opacity: isToday ? 1 : 0.5 }} />
                        {(daily.length <= 31 || d.metric_date.endsWith("01") || d.metric_date.endsWith("15") || isToday) && (
                          <div style={{ fontSize: 9, color: isToday ? L.text : L.dim, marginTop: 4, whiteSpace: "nowrap", transform: "rotate(-45deg)", transformOrigin: "top left", position: "absolute", bottom: -20, left: 4 }}>{dateStr}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 8, fontSize: 11, color: L.muted }}>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, background: L.text, borderRadius: 2, marginRight: 4 }} />Sessions</span>
                </div>
              </>
            )}
          </div>

          {/* Status breakdown */}
          <div style={card}>
            {secHead("Session status breakdown")}
            <div style={{ display: "flex", gap: 24 }}>
              {[
                { label: "Active", value: s.active || 0, c: "#1d4ed8" },
                { label: "Assigned", value: assignOverview?.assigned || 0, c: "#6d28d9" },
                { label: "Resolved", value: resolved, c: "#15803d" },
                { label: "Escalated", value: s.escalated || 0, c: "#b45309" },
              ].map(item => {
                const pct = totalSessions > 0 ? Math.round((item.value / totalSessions) * 100) : 0;
                return (
                  <div key={item.label} style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: L.muted }}>{item.label}</span>
                      <span style={{ fontSize: 12, color: item.c, fontWeight: 600 }}>{item.value} ({pct}%)</span>
                    </div>
                    <BarTrack pct={pct} color={item.c} />
                  </div>
                );
              })}
            </div>
          </div>
        </>)}

        {/* ═══ CONVERSATION INTELLIGENCE ═══ */}
        {section === "intelligence" && (<>
          {sentiment && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
              {["positive", "neutral", "negative", "frustrated", "unanalyzed"].map(key => (
                <div key={key} style={{ ...card, padding: "16px 18px", background: SENTIMENT_BG[key] }}>
                  <div style={{ fontSize: 10, color: SENTIMENT_COLORS[key], textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 6 }}>{SENTIMENT_ICONS[key] || "?"} {key}</div>
                  <div style={{ fontSize: 26, fontWeight: 300, color: SENTIMENT_COLORS[key], lineHeight: 1 }}>{sentiment[key] || 0}</div>
                  {sentiment.total > 0 && <div style={{ fontSize: 11, color: L.muted, marginTop: 4 }}>{Math.round(((sentiment[key] || 0) / sentiment.total) * 100)}%</div>}
                </div>
              ))}
            </div>
          )}

          {sentimentTrend.length > 0 && (
            <div style={{ ...card, marginBottom: 20 }}>
              {secHead("Sentiment trend")}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 110, overflowX: "auto", paddingBottom: 8 }}>
                {sentimentTrend.map(d => {
                  const total = (d.positive || 0) + (d.neutral || 0) + (d.negative || 0) + (d.frustrated || 0);
                  if (total === 0) return null;
                  return (
                    <div key={d.metric_date} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: `0 0 ${Math.max(100 / sentimentTrend.length, 4)}%`, minWidth: 14 }} title={`${new Date(d.metric_date).toLocaleDateString()}: ${total}`}>
                      <div style={{ display: "flex", flexDirection: "column", height: 90 }}>
                        {[{ key: "frustrated", val: d.frustrated || 0 }, { key: "negative", val: d.negative || 0 }, { key: "neutral", val: d.neutral || 0 }, { key: "positive", val: d.positive || 0 }].map(seg => {
                          const h = total > 0 ? (seg.val / total) * 90 : 0;
                          return h > 0 ? <div key={seg.key} style={{ width: 12, height: h, background: SENTIMENT_COLORS[seg.key], opacity: 0.75 }} /> : null;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 16, justifyContent: "center", marginTop: 10, fontSize: 11, color: L.muted }}>
                {["positive", "neutral", "negative", "frustrated"].map(key => (
                  <span key={key}><span style={{ display: "inline-block", width: 8, height: 8, background: SENTIMENT_COLORS[key], borderRadius: 2, marginRight: 4 }} />{key}</span>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div style={card}>
              {secHead("Top topics (AI tags)")}
              {intentTags.length === 0 ? empty("Run AI Analysis on conversations to see topic tags.") :
                <RankedList items={intentTags.slice(0, 12)} labelKey="tag" valueKey="count" maxValue={intentTags[0]?.count} color="#6d28d9" />}
            </div>
            <div style={card}>
              {secHead("Top intents (primary)")}
              {intents.length === 0 ? empty("No intent data yet.") :
                <RankedList items={intents.slice(0, 10)} labelKey="intent_label" valueKey="session_count" maxValue={intents[0]?.session_count} color={L.text} />}
            </div>
          </div>

          <div style={card}>
            {secHead("Content gaps — unanswered questions")}
            <p style={{ fontSize: 12, color: L.muted, marginBottom: 14, marginTop: 0 }}>Questions the AI couldn't answer confidently — content creation opportunities.</p>
            {gaps.length === 0 ? empty("No content gaps detected yet.") : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {gaps.slice(0, 10).map(gap => (
                  <div key={gap.id} style={{ padding: "12px 14px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}` }}>
                    <div style={{ fontSize: 13, color: L.text, lineHeight: 1.5, marginBottom: 4 }}>{gap.query_text}</div>
                    <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
                      <span style={{ color: "#b45309", fontWeight: 600 }}>{gap.occurrence_count}x</span>
                      {gap.query_category && <span style={{ color: L.muted }}>{gap.query_category}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>)}

        {/* ═══ PRODUCT INSIGHTS ═══ */}
        {section === "products" && (<>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div style={card}>
              {secHead("Most recommended products")}
              {productMentions.length === 0 ? empty("No product recommendation data yet.") : productMentions.slice(0, 12).map((p, i) => (
                <div key={p.product_id || i} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
                  <span style={{ fontSize: 10, color: L.dim, fontWeight: 600, width: 16, textAlign: "right" }}>{i + 1}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 12 }}>{p.product_name || "Unknown"}</span>
                      <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
                        <span style={{ color: L.muted, fontVariantNumeric: "tabular-nums" }}>{p.mention_count}x</span>
                        {p.avg_relevance && <span style={{ color: L.dim }}>{(p.avg_relevance * 100).toFixed(0)}%</span>}
                      </div>
                    </div>
                    <BarTrack pct={(p.mention_count / (productMentions[0]?.mention_count || 1)) * 100} color={L.text} />
                  </div>
                </div>
              ))}
            </div>

            <div style={card}>
              {secHead("Unanswered product questions")}
              <p style={{ fontSize: 12, color: L.muted, marginBottom: 14, marginTop: 0 }}>Questions where the AI found no matching products.</p>
              {lowMatch.length === 0 ? empty("No unmatched queries found.") : lowMatch.map((q, i) => (
                <div key={i} style={{ padding: "10px 14px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}`, marginBottom: 8 }}>
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>"{q.user_query}"</div>
                  <div style={{ display: "flex", gap: 10, fontSize: 11, marginTop: 4 }}>
                    <span style={{ color: "#b45309", fontWeight: 600 }}>Asked {q.times_asked}x</span>
                    <span style={{ color: L.muted }}>Avg results: {q.avg_results}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={card}>
            {secHead("Product catalog opportunities")}
            <p style={{ fontSize: 12, color: L.muted, marginBottom: 14, marginTop: 0 }}>Recurring questions that suggest products or content your catalog is missing.</p>
            {productOpps.length === 0 ? empty("No product opportunities detected yet.") : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                {productOpps.map((opp, i) => (
                  <div key={i} style={{ padding: "12px 14px", background: L.inputBg, borderRadius: 10, border: `1px solid ${L.borderLight}` }}>
                    <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 6 }}>{opp.query_text}</div>
                    <div style={{ display: "flex", gap: 8, fontSize: 11 }}>
                      <span style={{ color: "#b45309", fontWeight: 600 }}>{opp.occurrence_count}x</span>
                      {opp.category && <span style={{ padding: "1px 6px", background: L.borderLight, borderRadius: 4, color: L.muted }}>{opp.category}</span>}
                      <span style={{ color: L.dim }}>Last: {new Date(opp.last_seen).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>)}

        {/* ═══ WIDGET & FUNNEL ═══ */}
        {section === "funnel" && (<>
          {funnel && (
            <div style={{ ...card, marginBottom: 20 }}>
              {secHead("Conversation funnel")}
              <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
                {[
                  { label: "Sessions started", value: funnel.total_sessions || 0 },
                  { label: "With messages", value: funnel.with_messages || 0 },
                  { label: "AI engaged", value: funnel.with_ai_response || 0 },
                  { label: "Multi-turn (4+)", value: funnel.multi_turn || 0 },
                  { label: "Assigned", value: funnel.assigned || 0 },
                  { label: "Resolved", value: funnel.resolved || 0 },
                ].map((step, i, arr) => {
                  const maxVal = arr[0].value || 1;
                  const pct = maxVal > 0 ? Math.round((step.value / maxVal) * 100) : 0;
                  return (
                    <div key={step.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                      <div style={{ fontSize: 22, fontWeight: 300, letterSpacing: "-0.02em" }}>{step.value}</div>
                      <div style={{ width: "100%", height: 6, background: L.borderLight, borderRadius: 3 }}>
                        <div style={{ height: 6, borderRadius: 3, background: L.text, width: `${Math.max(pct, 4)}%`, opacity: 0.15 + (pct / 100) * 0.85, transition: "width 0.3s" }} />
                      </div>
                      <div style={{ fontSize: 10, color: L.muted, textAlign: "center" }}>{step.label}</div>
                      {i > 0 && <div style={{ fontSize: 10, color: pct >= 50 ? "#15803d" : pct >= 25 ? "#b45309" : "#dc2626", fontWeight: 500 }}>{pct}%</div>}
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 24, marginTop: 18, justifyContent: "center" }}>
                {funnel.avg_messages && <div style={{ fontSize: 12, color: L.muted }}>Avg messages: <span style={{ color: L.text, fontWeight: 500 }}>{funnel.avg_messages}</span></div>}
                {funnel.avg_duration_min && <div style={{ fontSize: 12, color: L.muted }}>Avg duration: <span style={{ color: L.text, fontWeight: 500 }}>{funnel.avg_duration_min}m</span></div>}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div style={card}>
              {secHead("Busiest hours")}
              {hourly.length === 0 ? empty("No hourly data yet.") : (
                <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 90 }}>
                  {Array.from({ length: 24 }, (_, h) => {
                    const data = hourly.find(d => d.hour_of_day === h);
                    const count = data?.session_count || 0;
                    const maxH = Math.max(1, ...hourly.map(d => d.session_count || 0));
                    const barH = maxH > 0 ? Math.max(2, (count / maxH) * 70) : 2;
                    return (
                      <div key={h} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }} title={`${h}:00 — ${count} sessions`}>
                        <div style={{ width: "70%", height: barH, background: count > 0 ? L.text : L.borderLight, borderRadius: "2px 2px 0 0", opacity: count > 0 ? 0.6 : 0.3 }} />
                        {h % 4 === 0 && <div style={{ fontSize: 9, color: L.dim, marginTop: 4 }}>{h}h</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={card}>
              {secHead("Top pages")}
              {topPages.length === 0 ? empty("No page data yet.") : topPages.slice(0, 8).map((p, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${L.borderLight}` }}>
                  <span style={{ fontSize: 12, color: L.textSoft, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "65%", fontFamily: "monospace" }}>{p.page_url?.replace(/^https?:\/\//, "")}</span>
                  <div style={{ display: "flex", gap: 10, fontSize: 11, flexShrink: 0 }}>
                    <span style={{ fontWeight: 600 }}>{p.session_count}</span>
                    <span style={{ color: L.muted }}>{p.avg_messages} avg</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>)}

        {/* ═══ TEAM PERFORMANCE ═══ */}
        {section === "team" && (<>
          {assignOverview && (
            <div style={{ display: "flex", gap: 0, marginBottom: 20, borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}` }}>
              {[
                { label: "Total conversations", value: assignOverview.total || 0 },
                { label: "Assigned", value: assignOverview.assigned || 0 },
                { label: "Unassigned (open)", value: assignOverview.unassigned || 0, warn: (assignOverview.unassigned || 0) > 0 },
                { label: "Avg time to resolve", value: assignOverview.avg_time_to_resolve_hours ? `${assignOverview.avg_time_to_resolve_hours}h` : "—" },
              ].map((c, i) => (
                <div key={c.label} style={{ padding: "16px 20px", flex: 1, borderRight: i < 3 ? `1px solid ${L.border}` : "none" }}>
                  <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted, marginBottom: 4 }}>{c.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 300, letterSpacing: "-0.02em", color: c.warn ? "#b45309" : L.text }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          <div style={card}>
            {secHead("Team member performance")}
            {teamPerf.length === 0 ? empty("No assignment data yet. Assign conversations to team members to see performance metrics.") : (
              <div style={{ borderRadius: 10, border: `1px solid ${L.border}`, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr", padding: "10px 18px", background: L.inputBg, fontSize: 10, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                  <span>Assignee</span><span style={{ textAlign: "center" }}>Assigned</span><span style={{ textAlign: "center" }}>Resolved</span><span style={{ textAlign: "center" }}>Open</span><span style={{ textAlign: "center" }}>Escalated</span><span style={{ textAlign: "center" }}>Avg resolve</span><span style={{ textAlign: "center" }}>Notes</span>
                </div>
                {teamPerf.map(t => {
                  const resolveRate = t.total_assigned > 0 ? Math.round((t.resolved / t.total_assigned) * 100) : 0;
                  return (
                    <div key={t.assignee} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr", padding: "12px 18px", borderTop: `1px solid ${L.borderLight}`, alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{t.assignee}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, textAlign: "center" }}>{t.total_assigned}</span>
                      <div style={{ textAlign: "center" }}>
                        <span style={{ fontSize: 13, color: "#15803d", fontWeight: 600 }}>{t.resolved}</span>
                        <span style={{ fontSize: 10, color: L.dim, marginLeft: 4 }}>({resolveRate}%)</span>
                      </div>
                      <span style={{ fontSize: 13, color: "#1d4ed8", textAlign: "center" }}>{t.open}</span>
                      <span style={{ fontSize: 13, color: t.escalated > 0 ? "#b45309" : L.dim, textAlign: "center" }}>{t.escalated}</span>
                      <span style={{ fontSize: 12, color: L.muted, textAlign: "center" }}>{t.avg_resolve_hours ? `${t.avg_resolve_hours}h` : "—"}</span>
                      <span style={{ fontSize: 12, color: L.muted, textAlign: "center" }}>{t.total_notes || 0}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>)}

        {/* ═══ MARKETING ═══ */}
        {section === "marketing" && <MarketingTab days={days} color={color} />}

        </div>
      </div>
    </Layout>
  );
}
